import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Person from "@mui/icons-material/Person";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";

import Activity from "components/DigiHapi/Activity.js";
import {
	ForgotPasswordButton,
	SubmitButton,
	loginCardSx
} from "components/DigiHapi/loginStyles";

import {
	fetchLogin,
	fetchForgetPassword,
	fetchLoginUsingToken,
	fetchLoginWithApple,
	fetchLoginWithGoogle,
	fetchLoginWithFB
} from "../../actions/login";
import { fetchGetUser } from "../../actions/profile";
import { signInWithApple, isAppleCancel } from "../../services/appleWebAuth";
import { signInWithGoogle, isGoogleCancel } from "../../services/googleWebAuth";
import {
	signInWithFacebook,
	isFacebookCancel
} from "../../services/facebookWebAuth";
import {
	SET_EMAIL_OR_PSEUDO,
	SET_PASSWORD,
	CLEAR_VALIDATE,
	VALIDATE_INPUTS,
	SET_LOGIN_WITH_FB_RESULT,
	SET_LOGIN_WITH_FB_ERROR,
	LOGIN_FAILURE,
	ROLE_WEBMASTER,
	ROLE_ADMINISTRATOR,
	ROLE_EMPLOYEE,
	CLEAR_FORGET_PWD_VALIDATE,
	VALIDATE_FORGET_PWD_INPUT
} from "../../actions/types";
import { parse } from "search-params";

// Redux
import { useDispatch, useSelector } from "react-redux";

import CustomFilledInput from "components/DigiHapi/CustomFilledInput";
import SocialLoginButtons from "components/DigiHapi/SocialLoginButtons";
import HomeHeader from "components/DigiHapi/HomeHeader";
import HomeFooter from "components/DigiHapi/HomeFooter";
import FullPageImage from "components/DigiHapi/FullPageImage";
import SnackBarContainer from "components/DigiHapi/SnackBarContainer";

const { REACT_APP_FB_CLIENT_ID, REACT_APP_SERVER_URL } = process.env;

export default function AdminLoginPage(props) {
	const { location, history } = props;

	const dispatch = useDispatch();

	const {
		isFetching,
		emailOrPseudo,
		emailOrPseudoErrorMsg,
		password,
		passwordErrorMsg,
		valid,
		snackInfoMsg,
		snackErrorMsg,
		clearValidate,
		strategy,
		validForgetPwd,
		clearForgetPwdValidate
	} = useSelector(state => state.loginReducer);

	const { user } = useSelector(state => state.profileReducer);
	const error = emailOrPseudoErrorMsg || passwordErrorMsg || snackErrorMsg;

	useEffect(() => {
		if (location.search) {
			const { token, communityId } = parse(location.search);
			if (token) {
				dispatch(fetchLoginUsingToken({ token, communityId }));
			}
		} else {
			dispatch(fetchGetUser());
			const emailOrPseudo = localStorage.getItem("emailOrPseudo");
			if (emailOrPseudo) {
				dispatch({
					type: SET_EMAIL_OR_PSEUDO,
					value: emailOrPseudo
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (clearForgetPwdValidate) {
			dispatch({
				type: VALIDATE_FORGET_PWD_INPUT
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clearForgetPwdValidate]);

	useEffect(() => {
		if (clearValidate) {
			dispatch({
				type: VALIDATE_INPUTS
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clearValidate]);

	useEffect(() => {
		if (valid) {
			dispatch(fetchLogin(emailOrPseudo, password));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [valid]);

	useEffect(() => {
		if (validForgetPwd) {
			dispatch(fetchForgetPassword(emailOrPseudo));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [validForgetPwd]);

	useEffect(() => {
		if (!error && user) {
			if (user.role && user.role === ROLE_WEBMASTER) {
				history.push("/webmaster/audits");
			} else if (
				user.role === ROLE_ADMINISTRATOR ||
				user.role === ROLE_EMPLOYEE
			) {
				if (strategy === "jwt") {
					history.push("/team/change-password");
				} else {
					history.push("/team/community");
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	// Sign in with Apple. The popup must be triggered by this direct click
	// gesture (browsers block popups opened from async/redux roundtrips), so we
	// open it here and only then dispatch the login. A user-cancelled popup is
	// silently ignored; any other failure surfaces a generic error.
	const handleAppleLogin = async () => {
		try {
			const result = await signInWithApple();
			dispatch(fetchLoginWithApple(result));
		} catch (e) {
			if (!isAppleCancel(e)) {
				dispatch({ type: LOGIN_FAILURE, payload: { message: "apple_error" } });
			}
		}
	};

	// Sign in with Google. Same gesture constraint as Apple: the GIS popup is
	// opened from this click handler. A user-cancelled popup is ignored.
	const handleGoogleLogin = async () => {
		try {
			const result = await signInWithGoogle();
			dispatch(fetchLoginWithGoogle(result));
		} catch (e) {
			if (!isGoogleCancel(e)) {
				dispatch({ type: LOGIN_FAILURE, payload: { message: "google_error" } });
			}
		}
	};

	// Login with Facebook. No communityId: the admin/webmaster/employee role is
	// resolved server-side from the user's existing roles.
	const handleFacebookLogin = async () => {
		try {
			const { token } = await signInWithFacebook();
			dispatch(fetchLoginWithFB(undefined, token));
		} catch (e) {
			if (!isFacebookCancel(e)) {
				dispatch({
					type: LOGIN_FAILURE,
					payload: { message: "facebook_error" }
				});
			}
		}
	};

	return (
		<FullPageImage
			bgImage="home"
			child={
				<Grid container justifyContent="center">
					<HomeHeader bgDarkGray />
					<Grid item xs={12} sm={12} md={6} lg={4} px={2}>
						<form
							onSubmit={event => {
								event.preventDefault();
								dispatch({
									type: CLEAR_VALIDATE
								});
							}}
						>
							<Card sx={loginCardSx}>
								<SnackBarContainer
									place="bc"
									snackInfoMsg={snackInfoMsg}
									snackErrorMsg={snackErrorMsg}
								>
									<MDBox display="flex" flexDirection="column" px={2} pt={2}>
										<SocialLoginButtons
											onGoogle={handleGoogleLogin}
											onFacebook={handleFacebookLogin}
											onApple={handleAppleLogin}
											disabled={isFetching}
										/>
										<CustomFilledInput
											labelText="Email ou pseudo"
											id="email"
											error={emailOrPseudoErrorMsg !== null}
											helperText={
												emailOrPseudoErrorMsg
													? emailOrPseudoErrorMsg
													: undefined
											}
											formControlProps={{
												fullWidth: true
											}}
											inputProps={{
												value: emailOrPseudo ? emailOrPseudo : "",
												endAdornment: (
													<InputAdornment position="end">
														<Person />
													</InputAdornment>
												),
												disabled: isFetching,
												onChange: e =>
													dispatch({
														type: SET_EMAIL_OR_PSEUDO,
														value: e.target.value
													})
											}}
										/>
										<CustomFilledInput
											labelText="Mot de passe"
											id="password"
											error={passwordErrorMsg !== null}
											helperText={
												passwordErrorMsg ? passwordErrorMsg : undefined
											}
											formControlProps={{
												fullWidth: true
											}}
											inputProps={{
												value: password ? password : "",
												endAdornment: (
													<InputAdornment position="end">
														<Icon>lock_outline</Icon>
													</InputAdornment>
												),
												type: "password",
												autoComplete: "off",
												disabled: isFetching,
												onChange: e =>
													dispatch({
														type: SET_PASSWORD,
														value: e.target.value
													})
											}}
										/>
									</MDBox>
								</SnackBarContainer>
								<MDBox
									display="flex"
									flexDirection="column"
									alignItems="center"
									px={2}
									pb={2}
								>
									<ForgotPasswordButton
										disabled={isFetching}
										onClick={() =>
											dispatch({
												type: CLEAR_FORGET_PWD_VALIDATE
											})
										}
									>
										{"Perte de mot de passe ?"}
									</ForgotPasswordButton>
									<SubmitButton disabled={isFetching}>
										{isFetching ? (
											<Activity />
										) : (
											<Icon
												sx={{ fontWeight: 900, fontSize: "40px !important" }}
											>
												arrow_forward
											</Icon>
										)}
									</SubmitButton>
								</MDBox>
							</Card>
						</form>
					</Grid>
					<HomeFooter bgDarkGray />
				</Grid>
			}
		/>
	);
}
